import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ShieldCheck, Scale, Gavel, Layers, SlidersHorizontal, Fingerprint } from 'lucide-react'
import { reconciliationService } from '@/services/reconciliation'

const pct = (v: number | null) => (v === null ? '—' : `${(v * 100).toFixed(1)}%`)
const x = (v: number | null) => (v === null ? '—' : `${v.toFixed(2)}x`)
const mm = (v: number) => `$${(v / 1e6).toFixed(0)}MM`

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string
  icon: typeof Scale
  children: React.ReactNode
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className="text-amber-300" />
        <span className="terminal-kicker">{title}</span>
      </div>
      {children}
    </div>
  )
}

export default function CreditBox() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['credit-box'],
    queryFn: () => reconciliationService.getCreditBox(),
    staleTime: 60_000,
  })

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-amber-400/10 border border-amber-400/20">
            <ShieldCheck size={17} className="text-amber-300" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-stone-100">
              Credit Box {data ? `v${data.rules.version}` : ''}
            </h1>
            <p className="text-xs text-stone-500">
              The versioned ruleset every deal is screened against. Rules as data — not code, not opinion.
            </p>
          </div>
        </div>
        <Link
          to="/golden-deal-reconciliation"
          className="text-[11px] text-amber-300 hover:text-amber-200 font-mono"
        >
          ← Golden-Deal Recon
        </Link>
      </div>

      {isLoading && <div className="card p-6 text-[12px] text-stone-500 font-mono">Loading the ruleset…</div>}
      {isError && <div className="card p-6 text-[12px] text-red-400 font-mono">Failed to load the credit box.</div>}

      {data && (
        <>
          <div className="card p-4">
            <div className="text-[12px] text-stone-400 leading-relaxed max-w-4xl">{data.rules.source}</div>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-[11px] font-mono">
              <span className="text-stone-500">
                status: <span className="text-amber-300">{data.rules.status.replace(/_/g, ' ')}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 text-stone-500">
                <Fingerprint size={12} className="text-amber-300" />
                artifact sha256: <span className="text-stone-300">{data.artifact_sha256.slice(0, 16)}…</span>
              </span>
            </div>
            <p className="text-[11px] text-stone-500 mt-2 max-w-4xl leading-relaxed">{data.note}</p>
          </div>

          {/* Programs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(data.rules.programs).map(([key, p]) => (
              <Section key={key} title={p.name} icon={Scale}>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {[
                    ['Max LTV', `${pct(p.max_ltv)} (${p.ltv_basis.replace(/_/g, ' ')})`],
                    ['Min debt yield (stabilized)', pct(p.min_debt_yield_stabilized)],
                    ['Min DSCR (amortizing)', x(p.min_dscr_amortizing)],
                    ['Loan size', `${mm(p.loan_size_min)} – ${mm(p.loan_size_max)}`],
                    [
                      'Asset classes',
                      Array.isArray(p.asset_classes) ? p.asset_classes.join(', ') : p.asset_classes,
                    ],
                    ['Construction', p.construction ? 'in scope' : 'out of scope'],
                    ['Geographies', p.geographies.join(' · ')],
                  ].map(([label, value]) => (
                    <div key={label as string} className="border-b border-amber-400/[0.05] pb-1">
                      <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-stone-500">
                        {label}
                      </div>
                      <div className="text-[13px] font-mono text-stone-200">{value}</div>
                    </div>
                  ))}
                </div>
              </Section>
            ))}
          </div>

          {/* Codified decisions */}
          <Section title="Codified Decisions" icon={Gavel}>
            <div className="space-y-2">
              {Object.entries(data.rules.decisions).map(([k, v]) => (
                <div key={k} className="flex gap-3 border-b border-amber-400/[0.05] pb-2">
                  <span className="w-40 flex-shrink-0 text-[11px] font-mono uppercase tracking-[0.1em] text-stone-500">
                    {k.replace(/_/g, ' ')}
                  </span>
                  <span className="text-[12px] text-stone-300 leading-relaxed">{v}</span>
                </div>
              ))}
            </div>
          </Section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Conventions */}
            <Section title="Conventions" icon={SlidersHorizontal}>
              <div className="space-y-1.5">
                {Object.entries(data.rules.conventions).map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-3 border-b border-amber-400/[0.05] py-1">
                    <span className="text-[11px] font-mono text-stone-500">{k.replace(/_/g, ' ')}</span>
                    <span className="text-[12px] font-mono text-stone-200 text-right">
                      {Array.isArray(v) ? v.join(', ') : String(v)}
                    </span>
                  </div>
                ))}
              </div>
            </Section>

            {/* Stack + judgment */}
            <div className="space-y-4">
              <Section title="Tranche Seniority — Attachment Order" icon={Layers}>
                <div className="flex flex-wrap items-center gap-2">
                  {data.rules.tranche_seniority.map((t, i) => (
                    <span key={t} className="inline-flex items-center gap-2 text-[12px] font-mono">
                      <span className="px-2 py-0.5 rounded border border-amber-400/20 bg-amber-400/[0.05] text-stone-200">
                        {t.replace(/_/g, ' ')}
                      </span>
                      {i < data.rules.tranche_seniority.length - 1 && (
                        <span className="text-stone-600">→</span>
                      )}
                    </span>
                  ))}
                </div>
                <p className="text-[11px] text-stone-500 mt-2 leading-relaxed">
                  Each tranche is screened as itself plus all senior debt — cumulative attachment basis.
                </p>
              </Section>

              <Section title="Judgment Factors — Always Referred" icon={Gavel}>
                <div className="flex flex-wrap gap-2">
                  {data.rules.judgment_factors.map((f) => (
                    <span
                      key={f}
                      className="text-[11px] font-mono px-2 py-1 rounded border border-sky-400/25 bg-sky-400/[0.05] text-sky-300"
                    >
                      {f.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
                <p className="text-[11px] text-stone-500 mt-2 leading-relaxed">
                  These are never decided by the screen. No hard disqualifiers exist: judgment routes to
                  your people, every time.
                </p>
              </Section>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
